"use strict";
import { TradeType, CurrencyAmount, Percent, Price, Fraction } from "@uniswap/sdk-core";
import { ONE } from "@uniswap/router-sdk";
import { useCallback, useMemo, useState } from "react";
import { useAccountMeta } from "contexts/UniswapContext";
import { useEnabledChains } from "features/chains/hooks/useEnabledChains";
import { useCurrencyInfo } from "features/tokens/useCurrencyInfo";
import { buildCurrencyId } from "utils/uniswapCurrencyId";
import { useQuery } from "@tanstack/react-query";
import { useTransactionSettingsContext } from "features/transactions/settings/contexts/TransactionSettingsContext";
import {
  fetchOpenOceanQuote,
  fetchOpenOceanSwap
} from "features/routing/api/openOceanApi";
import { OPENOCEAN_NATIVE_TOKEN, getOpenOceanChainPath } from "features/openocean/constants";
import { QuoteMethod } from "state/routing/types";
function parsePriceImpact(value) {
  if (!value) {
    return void 0;
  }
  const cleaned = value.replace("%", "").trim();
  if (cleaned === "") {
    return void 0;
  }
  const numeric = Number(cleaned);
  if (!Number.isFinite(numeric) || numeric < -100 || numeric > 100) {
    return void 0;
  }
  return new Percent(Math.round(numeric * 100), 1e4);
}
function createOpenOceanTradeObject(response, currencyIn, currencyOut, tradeType, slippageTolerance) {
  const inputAmount = CurrencyAmount.fromRawAmount(currencyIn, response.inAmount);
  const outputAmount = CurrencyAmount.fromRawAmount(currencyOut, response.outAmount);
  const minOutRaw = "minOutAmount" in response ? response.minOutAmount : void 0;
  const minBuyAmount = minOutRaw ? CurrencyAmount.fromRawAmount(currencyOut, minOutRaw) : outputAmount;
  const executionPrice = new Price(
    currencyIn,
    currencyOut,
    inputAmount.quotient.toString(),
    outputAmount.quotient.toString()
  );
  const slippagePercent = new Percent(Math.floor(slippageTolerance * 100), 1e4);
  const gasEstimate = response.estimatedGas ? parseInt(String(response.estimatedGas), 10) : 2e5;
  return {
    inputAmount,
    outputAmount,
    tradeType,
    executionPrice,
    minimumAmountOut(_slippage) {
      if (tradeType === TradeType.EXACT_OUTPUT) {
        return outputAmount;
      }
      if (!_slippage && minBuyAmount) {
        return minBuyAmount;
      }
      const st = _slippage || slippagePercent;
      const adjusted = new Fraction(ONE).subtract(st).multiply(outputAmount.quotient).quotient;
      return CurrencyAmount.fromRawAmount(currencyOut, adjusted);
    },
    maximumAmountIn(slippage) {
      const st = slippage || slippagePercent;
      if (tradeType === TradeType.EXACT_INPUT) {
        return inputAmount;
      }
      const adjusted = new Fraction(ONE).add(st).multiply(inputAmount.quotient).quotient;
      return CurrencyAmount.fromRawAmount(currencyIn, adjusted);
    },
    worstExecutionPrice(slippage) {
      const st = slippage || slippagePercent;
      const minOut = tradeType === TradeType.EXACT_OUTPUT ? outputAmount : minBuyAmount || CurrencyAmount.fromRawAmount(
        currencyOut,
        new Fraction(ONE).subtract(st).multiply(outputAmount.quotient).quotient
      );
      return new Price(currencyIn, currencyOut, inputAmount.quotient.toString(), minOut.quotient.toString());
    },
    inputTax: new Percent(0),
    outputTax: new Percent(0),
    priceImpact: parsePriceImpact(response.price_impact),
    gasUseEstimate: gasEstimate,
    gasUseEstimateUSD: "0.01",
    quoteMethod: QuoteMethod.CLIENT_SIDE_FALLBACK,
    approveInfo: { needsApprove: false },
    routing: "CLASSIC",
    fillType: "classic",
    isOpenOcean: true,
    openOceanResponse: response,
    v2Routes: [],
    v3Routes: [],
    mixedRoutes: [],
    swaps: [],
    routes: [],
    postSwapFeeOutputAmount: outputAmount,
    swapFee: void 0,
    blockNumber: void 0,
    requestId: void 0,
    totalGasUseEstimateUSD: void 0,
    slippageTolerance,
    slippageTolerancePercent: slippagePercent
  };
}
export function useOpenOceanQuote({
  tokenIn,
  tokenOut,
  amountSpecified,
  tradeType,
  chainId,
  skip = false,
  currencyIn: currencyInOverride,
  currencyOut: currencyOutOverride
}) {
  const account = useAccountMeta();
  const { defaultChainId } = useEnabledChains();
  const effectiveChainId = chainId ?? defaultChainId;
  const { customSlippageTolerance, autoSlippageTolerance } = useTransactionSettingsContext();
  const slippageValue = customSlippageTolerance ?? autoSlippageTolerance ?? 0.5;
  const currencyInInfo = useCurrencyInfo(
    tokenIn && effectiveChainId ? buildCurrencyId(effectiveChainId, tokenIn) : void 0
  );
  const currencyOutInfo = useCurrencyInfo(
    tokenOut && effectiveChainId ? buildCurrencyId(effectiveChainId, tokenOut) : void 0
  );
  const currencyIn = currencyInOverride ?? currencyInInfo?.currency;
  const currencyOut = currencyOutOverride ?? currencyOutInfo?.currency;
  const inTokenAddress = currencyIn?.isNative ? OPENOCEAN_NATIVE_TOKEN : tokenIn;
  const outTokenAddress = currencyOut?.isNative ? OPENOCEAN_NATIVE_TOKEN : tokenOut;
  const shouldSkip = useMemo(
    () => skip || !tokenIn || !tokenOut || !amountSpecified || amountSpecified === "0" || !currencyIn || !currencyOut || tokenIn === tokenOut || tradeType !== TradeType.EXACT_INPUT || !getOpenOceanChainPath(effectiveChainId),
    [skip, tokenIn, tokenOut, amountSpecified, currencyIn, currencyOut, tradeType, effectiveChainId]
  );
  const hasAccount = Boolean(account?.address);
  const [refreshKey, setRefreshKey] = useState(0);
  const refetch = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);
  const { data, isLoading, error, dataUpdatedAt } = useQuery({
    queryKey: [
      "openocean-quote",
      effectiveChainId,
      inTokenAddress,
      outTokenAddress,
      amountSpecified,
      slippageValue,
      hasAccount,
      account?.address,
      refreshKey
    ],
    queryFn: async () => {
      if (!inTokenAddress || !outTokenAddress || !amountSpecified) {
        return null;
      }
      const params = {
        chainId: effectiveChainId,
        inTokenAddress,
        outTokenAddress,
        amountDecimals: amountSpecified,
        slippagePercent: slippageValue,
        account: account?.address
      };
      if (account?.address) {
        return fetchOpenOceanSwap(params);
      }
      return fetchOpenOceanQuote(params);
    },
    enabled: !shouldSkip,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: 1
  });
  const quoteUpdatedAt = dataUpdatedAt || null;
  const trade = useMemo(() => {
    if (!data || !currencyIn || !currencyOut) {
      return null;
    }
    return createOpenOceanTradeObject(data, currencyIn, currencyOut, tradeType, slippageValue);
  }, [data, currencyIn, currencyOut, tradeType, slippageValue]);
  return useMemo(
    () => ({
      trade,
      isLoading,
      error,
      refetch,
      quoteUpdatedAt
    }),
    [trade, isLoading, error, refetch, quoteUpdatedAt]
  );
}
